import { useEffect, useState } from "react";
import { FinancialDecision, IVehicle } from "../../models/Vehicle";
import { ILead } from "../../models/Lead";
import { IPart } from "../../models/Part";
import useVehiclesMutations from "../../data/useVehiclesMutations";
import useLeadMutations from "../../data/useLeadMutations";
import useVehiclePricingMatrix from "../../data/useVehiclePricingMatrix";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import classNames from "../../utils/classNames";
import PriceFormat from "../../utils/priceFormat";
import bestColumnTotal from "../../utils/bestColumnTotal";

const Row = ({
  label,
  value,
  bold = false,
  valueClassName,
}: {
  label: string;
  value: string;
  bold?: boolean;
  valueClassName?: string;
}) => (
  <div className="flex items-center justify-between py-1.5">
    <span className={classNames("text-sm", bold ? "font-semibold text-gray-900" : "text-gray-500")}>{label}</span>
    <span className={classNames("text-sm text-gray-900", bold && "font-semibold", valueClassName)}>{value}</span>
  </div>
);

const EditableRow = ({
  label,
  value,
  onChange,
  onBlur,
  disabled = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  disabled?: boolean;
}) => (
  <div className="flex items-center justify-between py-1.5">
    <span className="text-sm text-gray-500">{label}</span>
    <div className="relative w-28">
      <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-sm text-gray-400">£</span>
      <input
        type="number"
        step="0.01"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        disabled={disabled}
        placeholder="0.00"
        className="w-full rounded border border-gray-300 pl-5 pr-2 py-1 text-right text-sm focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-100 disabled:text-gray-400"
      />
    </div>
  </div>
);

export default function VehicleFinancialSummary({ vehicle, lead = null }: { vehicle: IVehicle; lead?: ILead | null }) {
  const { patchVehicle } = useVehiclesMutations();
  const { update: updateLead } = useLeadMutations(lead?.id ?? "", "VehicleFinancialSummary");
  const { vehiclePricingMatrix } = useVehiclePricingMatrix(vehicle.id, "VehicleFinancialSummary");
  const { saveHandlers } = useDefaultCRUDHandlers("Financial Decision");
  const { saveHandlers: priceHandlers } = useDefaultCRUDHandlers("Target Price");
  const { saveHandlers: leadPriceHandlers } = useDefaultCRUDHandlers("Vehicle Price");

  // Editable prices are held as local strings so the inputs stay responsive,
  // re-synced whenever the underlying record changes (e.g. after a save refetch).
  const [currentPriceInput, setCurrentPriceInput] = useState("");
  const [targetPriceInput, setTargetPriceInput] = useState("");

  useEffect(() => {
    setCurrentPriceInput(lead?.priceWithVAT != null ? String(lead.priceWithVAT) : "");
  }, [lead?.priceWithVAT]);

  useEffect(() => {
    setTargetPriceInput(vehicle.targetPrice != null ? String(vehicle.targetPrice) : "");
  }, [vehicle.targetPrice]);

  const parts = vehicle.parts ?? [];
  const partPrice = (part: IPart) => part.price ?? part.enteredPrice ?? 0;
  const coreValue = parts.filter((part) => part.isCore).reduce((sum, part) => sum + partPrice(part), 0);
  const partsValue = parts.filter((part) => !part.isCore).reduce((sum, part) => sum + partPrice(part), 0);
  const total = partsValue + coreValue;

  const potentialValue = bestColumnTotal(vehiclePricingMatrix.data?.data);

  // "Current price" is the lead's VAT-inclusive price; profit is the dismantling
  // total less what we pay for the vehicle.
  const currentPrice = currentPriceInput.trim() === "" ? 0 : Number(currentPriceInput);
  const safeCurrentPrice = Number.isNaN(currentPrice) ? 0 : currentPrice;
  const profit = total - safeCurrentPrice;
  const potentialProfit = potentialValue - safeCurrentPrice;

  const decision = vehicle.financialDecision ?? FinancialDecision.None;

  const setDecision = (next: FinancialDecision) => {
    // Clicking the active choice again clears it back to None.
    const value = decision === next ? FinancialDecision.None : next;
    patchVehicle.mutate({ vehicleId: vehicle.id, patch: { financialDecision: value } }, saveHandlers);
  };

  const handleCurrentPriceBlur = () => {
    if (!lead) return;

    const trimmed = currentPriceInput.trim();
    const parsed = trimmed === "" ? 0 : Number(trimmed);

    if (Number.isNaN(parsed)) {
      setCurrentPriceInput(lead.priceWithVAT != null ? String(lead.priceWithVAT) : "");
      return;
    }

    // The input shows the VAT-inclusive price; the lead stores the net price.
    const basePrice = lead.applyVAT ? parsed / 1.2 : parsed;
    if (basePrice === lead.price) return;

    updateLead.mutate({ ...lead, price: basePrice }, leadPriceHandlers);
  };

  const handleTargetPriceBlur = () => {
    const trimmed = targetPriceInput.trim();
    const parsed = trimmed === "" ? null : Number(trimmed);

    if (parsed != null && Number.isNaN(parsed)) {
      setTargetPriceInput(vehicle.targetPrice != null ? String(vehicle.targetPrice) : "");
      return;
    }

    if (parsed === (vehicle.targetPrice ?? null)) return;

    patchVehicle.mutate({ vehicleId: vehicle.id, patch: { targetPrice: parsed } }, priceHandlers);
  };

  const cardColour =
    decision === FinancialDecision.Target
      ? "border-green-300 bg-green-50"
      : decision === FinancialDecision.Rejected
        ? "border-red-300 bg-red-50"
        : decision === FinancialDecision.Purchased
          ? "border-blue-300 bg-blue-50"
          : "border-gray-200 bg-white";

  return (
    <div className={classNames("rounded-lg border shadow-sm p-4", cardColour)}>
      <h2 className="text-lg font-medium text-gray-900 mb-2">Financial Summary</h2>

      <EditableRow
        label="Current Price"
        value={currentPriceInput}
        onChange={setCurrentPriceInput}
        onBlur={handleCurrentPriceBlur}
        disabled={!lead || updateLead.isLoading}
      />
      <EditableRow
        label="Target Price"
        value={targetPriceInput}
        onChange={setTargetPriceInput}
        onBlur={handleTargetPriceBlur}
        disabled={patchVehicle.isLoading}
      />
      <Row label="Reserved Price" value={vehicle.reservedPrice != null ? PriceFormat(vehicle.reservedPrice) : "—"} />

      <hr className="my-2 border-gray-200" />
      <Row label="Potential Value" value={PriceFormat(potentialValue)} />
      <Row
        label="Potential Profit"
        value={PriceFormat(potentialProfit)}
        valueClassName={potentialProfit < 0 ? "text-red-600" : "text-green-700"}
      />

      <hr className="my-2 border-gray-200" />
      <Row label="Parts Value" value={PriceFormat(partsValue)} />
      <Row label="Core Value" value={PriceFormat(coreValue)} />
      <Row label="Scrap Price" value="—" />
      <Row label="Total" value={PriceFormat(total)} bold />
      <Row
        label="Profit"
        value={PriceFormat(profit)}
        bold
        valueClassName={profit < 0 ? "text-red-600" : "text-green-700"}
      />

      <div className="flex gap-2 pt-3">
        <button
          type="button"
          onClick={() => setDecision(FinancialDecision.Target)}
          disabled={patchVehicle.isLoading}
          className={classNames(
            "flex-1 rounded-md border px-3 py-2 text-sm font-semibold transition-colors disabled:opacity-60",
            decision === FinancialDecision.Target
              ? "border-transparent bg-green-600 text-white hover:bg-green-700"
              : "border-green-500 text-green-700 hover:bg-green-50"
          )}
        >
          Target
        </button>
        <button
          type="button"
          onClick={() => setDecision(FinancialDecision.Rejected)}
          disabled={patchVehicle.isLoading}
          className={classNames(
            "flex-1 rounded-md border px-3 py-2 text-sm font-semibold transition-colors disabled:opacity-60",
            decision === FinancialDecision.Rejected
              ? "border-transparent bg-red-600 text-white hover:bg-red-700"
              : "border-red-500 text-red-700 hover:bg-red-50"
          )}
        >
          Rejected
        </button>
        <button
          type="button"
          onClick={() => setDecision(FinancialDecision.Purchased)}
          disabled={patchVehicle.isLoading}
          className={classNames(
            "flex-1 rounded-md border px-3 py-2 text-sm font-semibold transition-colors disabled:opacity-60",
            decision === FinancialDecision.Purchased
              ? "border-transparent bg-blue-600 text-white hover:bg-blue-700"
              : "border-blue-500 text-blue-700 hover:bg-blue-50"
          )}
        >
          Purchased
        </button>
      </div>
    </div>
  );
}
